import {
  keepPreviousData,
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import { Block } from '@blocknote/core';
import { WorksheetBlockSchema } from 'pages/in-app/worksheet/Worksheet';
import { axios } from 'utils/axios';
import { queryClient } from 'utils/react-query';
import type { Worksheet } from './useWorksheets';

const WORKSHEET_TEMPLATES_QUERY_KEY = 'worksheet-templates';
const WORKSHEET_TEMPLATE_QUERY_KEY = 'worksheet-template';

export type WorksheetTemplateCreator = {
  id: string;
  firstName: string;
  lastName: string;
};

export type WorksheetTemplateListItem = {
  id: string;
  title: string;
  description: string | null;
  createdAt: string;
  updatedAt: string | null;
  createdBy: WorksheetTemplateCreator | null;
  isOwner: boolean;
  canDelete: boolean;
};

export type PaginatedWorksheetTemplatesResponse = {
  data: WorksheetTemplateListItem[];
  total: number;
  page: number;
  pageSize: number;
};

export type WorksheetTemplateDetail = {
  id: string;
  title: string;
  description: string | null;
  content: Block<WorksheetBlockSchema>[];
  createdAt: string;
  updatedAt: string | null;
  b2bSchoolId: string;
  createdById: string | null;
  sourceWorksheetId: string | null;
};

const getWorksheetTemplatesPage = async ({
  page = 1,
  pageSize = 10,
}: {
  page?: number;
  pageSize?: number;
}): Promise<PaginatedWorksheetTemplatesResponse> => {
  const { data } = await axios.get<PaginatedWorksheetTemplatesResponse>('/worksheet-templates', {
    params: { page, pageSize },
  });
  return data;
};

export const useGetWorksheetTemplates = ({
  page = 1,
  pageSize = 10,
  isDisabled = false,
}: {
  page?: number;
  pageSize?: number;
  isDisabled?: boolean;
}): UseQueryResult<PaginatedWorksheetTemplatesResponse, unknown> => {
  return useQuery({
    queryKey: [WORKSHEET_TEMPLATES_QUERY_KEY, page, pageSize],
    queryFn: () =>
      getWorksheetTemplatesPage({
        page,
        pageSize,
      }),
    placeholderData: keepPreviousData,
    enabled: !isDisabled,
  });
};

const getWorksheetTemplate = async ({
  templateId,
}: {
  templateId: string;
}): Promise<WorksheetTemplateDetail> => {
  const { data } = await axios.get<WorksheetTemplateDetail>(`/worksheet-templates/${templateId}`);
  return data;
};

export const useGetWorksheetTemplate = ({
  templateId,
}: {
  templateId: string | undefined;
}): UseQueryResult<WorksheetTemplateDetail, unknown> => {
  return useQuery({
    queryKey: [WORKSHEET_TEMPLATE_QUERY_KEY, templateId],
    queryFn: () =>
      getWorksheetTemplate({
        templateId: templateId as string,
      }),
    enabled: !!templateId,
  });
};

const copyWorksheetFromTemplate = async ({
  templateId,
  title,
}: {
  templateId: string;
  title: string;
}): Promise<Worksheet> => {
  const { data } = await axios.post<Worksheet>(`/worksheet-templates/copy/${templateId}`, {
    title,
  });
  return data;
};

export const useCopyWorksheetFromTemplate = (): UseMutationResult<
  Worksheet,
  unknown,
  { templateId: string; title: string },
  unknown
> => {
  return useMutation({
    mutationKey: ['copy-worksheet-from-template'],
    mutationFn: copyWorksheetFromTemplate,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['worksheets'] });
    },
  });
};

const createWorksheetTemplate = async ({
  sourceWorksheetId,
}: {
  sourceWorksheetId: string;
}): Promise<WorksheetTemplateDetail> => {
  const { data } = await axios.post<WorksheetTemplateDetail>(
    `/worksheet-templates/${sourceWorksheetId}`
  );
  return data;
};

export const useCreateWorksheetTemplate = (): UseMutationResult<
  WorksheetTemplateDetail,
  unknown,
  { sourceWorksheetId: string },
  unknown
> => {
  return useMutation({
    mutationKey: ['create-worksheet-template'],
    mutationFn: createWorksheetTemplate,
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [WORKSHEET_TEMPLATES_QUERY_KEY],
      });
    },
  });
};

const deleteWorksheetTemplate = async ({ templateId }: { templateId: string }): Promise<void> => {
  await axios.delete(`/worksheet-templates/${templateId}`);
};

export const useDeleteWorksheetTemplate = (): UseMutationResult<
  void,
  unknown,
  { templateId: string },
  unknown
> => {
  return useMutation({
    mutationKey: ['delete-worksheet-template'],
    mutationFn: deleteWorksheetTemplate,
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [WORKSHEET_TEMPLATES_QUERY_KEY],
      });
      await queryClient.removeQueries({
        queryKey: [WORKSHEET_TEMPLATE_QUERY_KEY, variables.templateId],
      });
    },
  });
};
